import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../services/api';

const SEARCH_DELAY_MS = 300;

export const getUserSearchDisplayName = (user) => String(
  user?.displayName || user?.nickname || user?.name || user?.phoneNumber || user?.phone || `#${user?.id || ''}`
).trim();

const getUserAccount = (user) => String(
  user?.account || user?.username || user?.email || user?.login || user?.phoneNumber || user?.phone || ''
).trim();

const getUserRoleSummary = (user) => {
  const fromRoles = Array.isArray(user?.roles)
    ? user.roles.map((role) => (typeof role === 'string' ? role : role?.name || role?.role || role?.code))
    : [];
  const directRoles = [user?.role, user?.userRole, user?.userType].filter(Boolean);
  return Array.from(new Set(
    [...fromRoles, ...directRoles].map((role) => String(role || '').trim()).filter(Boolean)
  )).join(', ');
};

export default function RoleUserSearchField({
  fieldId,
  label,
  placeholder,
  roles,
  selectedId,
  initialQuery = '',
  resetKey,
  onSelect,
  onClear
}) {
  const { t } = useTranslation();
  const roleKey = roles.join(',');
  const [query, setQuery] = useState(initialQuery);
  const [options, setOptions] = useState([]);
  const [status, setStatus] = useState('idle');
  const [retryToken, setRetryToken] = useState(0);
  const requestIdRef = useRef(0);
  const skipNextSearchRef = useRef(Boolean(initialQuery));

  useEffect(() => {
    requestIdRef.current += 1;
    skipNextSearchRef.current = Boolean(initialQuery);
    setQuery(initialQuery || '');
    setOptions([]);
    setStatus('idle');
  }, [initialQuery, resetKey]);

  useEffect(() => {
    const trimmedQuery = query.trim();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      setStatus('idle');
      return undefined;
    }

    if (!trimmedQuery) {
      setOptions([]);
      setStatus('idle');
      return undefined;
    }

    let active = true;
    setStatus('loading');
    const timer = setTimeout(async () => {
      try {
        const response = await adminAPI.searchUsers(trimmedQuery, roleKey.split(',').filter(Boolean));
        if (!active || requestId !== requestIdRef.current) return;
        if (!response.data?.success) throw new Error('USER_SEARCH_FAILED');
        const nextOptions = Array.isArray(response.data?.data) ? response.data.data : [];
        setOptions(nextOptions);
        setStatus(nextOptions.length > 0 ? 'ready' : 'empty');
      } catch (_error) {
        if (!active || requestId !== requestIdRef.current) return;
        setOptions([]);
        setStatus('error');
      }
    }, SEARCH_DELAY_MS);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, retryToken, roleKey]);

  const handleQueryChange = (event) => {
    if (selectedId) onClear();
    setQuery(event.target.value);
  };

  const handleSelect = (user) => {
    skipNextSearchRef.current = true;
    setQuery(`${getUserSearchDisplayName(user)} (ID: ${user.id})`);
    setOptions([]);
    setStatus('idle');
    onSelect(user.id, user);
  };

  const handleClear = () => {
    requestIdRef.current += 1;
    setQuery('');
    setOptions([]);
    setStatus('idle');
    onClear();
  };

  return (
    <div>
      <label htmlFor={fieldId} className="mb-2 block text-[10px] font-black uppercase text-slate-500">
        {label}
      </label>
      <div className="space-y-2">
        <input
          id={fieldId}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleQueryChange}
          autoComplete="off"
          className="w-full rounded-xl border border-white/10 bg-slate-700/50 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-cyan-400/50 focus:outline-none"
        />

        {status === 'loading' && (
          <div role="status" className="flex items-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3 text-xs font-semibold text-cyan-100">
            <Loader2 size={14} className="animate-spin" />
            {t('unitControl.userSearch.searching')}
          </div>
        )}
        {status === 'empty' && (
          <p role="status" className="rounded-xl border border-amber-400/20 bg-amber-400/5 p-3 text-xs font-semibold text-amber-100">
            {t('unitControl.userSearch.empty')}
          </p>
        )}
        {status === 'error' && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-rose-400/20 bg-rose-400/5 p-3 text-xs font-semibold text-rose-100">
            <span>{t('unitControl.userSearch.error')}</span>
            <button
              type="button"
              onClick={() => setRetryToken((value) => value + 1)}
              className="rounded-lg border border-rose-300/30 px-2 py-1 font-black hover:bg-rose-300/10"
            >
              {t('unitControl.userSearch.retry')}
            </button>
          </div>
        )}

        {options.length > 0 && (
          <div role="listbox" aria-label={t('unitControl.userSearch.results')} className="max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-slate-900/50">
            {options.map((user) => {
              const displayName = getUserSearchDisplayName(user);
              return (
                <button
                  key={user.id}
                  type="button"
                  role="option"
                  aria-selected="false"
                  aria-label={t('unitControl.userSearch.selectUser', { name: displayName, id: user.id })}
                  onClick={() => handleSelect(user)}
                  className="block w-full cursor-pointer border-b border-white/5 p-3 text-left last:border-b-0 hover:bg-slate-800"
                >
                  <span className="block text-sm font-bold text-white">{displayName}</span>
                  <span className="block text-xs text-slate-300">
                    {t('unitControl.userOptionMeta', {
                      id: user.id,
                      account: getUserAccount(user) || '-',
                      phone: user.phoneNumber || user.phone || '-',
                      role: getUserRoleSummary(user) || '-'
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {selectedId && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3">
            <p className="text-sm text-amber-400">{t('unitControl.selectedId', { id: selectedId })}</p>
            <button
              type="button"
              onClick={handleClear}
              className="rounded-lg p-1 text-amber-300 hover:bg-amber-500/20"
              title={t('common.clear', { defaultValue: 'Clear' })}
              aria-label={t('unitControl.userSearch.clearSelection', { id: selectedId })}
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
